import React, { useState } from 'react'; // NEW: Import useState
import { Composite } from '../types/costwand.d';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table.tsx';
import { Button } from './ui/button.tsx';
import Pagination from './ui/Pagination.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog.tsx';
import CompositionViewer from './CompositionViewer.tsx';
import { Link } from 'react-router-dom'; // Assuming react-router-dom for navigation
import { Spinner } from './ui/spinner.tsx'; // Import Spinner
import { useSurchargeLookup } from '../hooks/useSurchargeLookup.ts';
import { apiClient } from '../api/apiClient.ts'; // NEW: Import apiClient

interface CompositesTableProps {
  composites: Composite[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onEdit: (composite: Composite) => void; // For editing tax_profile_id
  onDelete: (id: number) => void;
  loading: boolean;
  error: string | null;
  baseEndpoint: string; // NEW: Add baseEndpoint prop
}

const CompositesTable: React.FC<CompositesTableProps> = ({
  composites,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  onEdit,
  onDelete,
  loading,
  error,
  baseEndpoint, // NEW: Destructure baseEndpoint
}) => {
  const { surcharges, loading: surchargesLoading, error: surchargesError } = useSurchargeLookup();
  const [exportLoading, setExportLoading] = useState(false); // NEW: State for export loading

  const handleExportCsv = async () => {
    setExportLoading(true);
    try {
      await apiClient<void>(baseEndpoint, { downloadCsv: true });
    } catch (err: any) {
      // Error already handled by apiClient and globalErrorHandler
    } finally {
      setExportLoading(false);
    }
  };

  if (loading)
    return (
      <p className="text-center text-muted-foreground flex items-center justify-center gap-2"><Spinner size="sm" /> Loading Composites...</p>
    );
  if (error)
    return (
      <p className="text-center text-destructive">Error: {error}</p>
    );
  if (composites.length === 0 && !loading)
    return (
      <p className="text-center text-muted-foreground">No Composites found.</p>
    );

  return (
    <div className="rounded-md border">
      <div className="flex justify-end mb-4"> {/* NEW: Wrapper for export button */}
        <Button onClick={handleExportCsv} disabled={loading || exportLoading} variant="outline">
          {exportLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Exporting...</span> : 'Export to CSV'}
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Description</TableHead>
            <TableHead>Article No.</TableHead>
            <TableHead>Family</TableHead>
            <TableHead>Surcharge Profile</TableHead> {/* Changed to Surcharge Profile */}
            <TableHead>Cost</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {composites.map((composite) => {
            const hasComposition =
              (composite.composition?.components && composite.composition.components.length > 0) ||
              (composite.composition?.processes && composite.composition.processes.length > 0);
              
              const surchargeProfile = surcharges.find(s => s.id === composite.tax_profile_id);
              const surchargePercentage = surchargeProfile?.percentage || 0;
              const costWithOutSurcharge = composite.cost ? composite.cost / (1 + surchargePercentage / 100) : null;
            return (
              <TableRow key={composite.id}>
                <TableCell>{composite.description}</TableCell>
                <TableCell>{composite.article_number}</TableCell>
                <TableCell>
                  {composite._labels?.family_id || `ID: ${composite.family_id}`}
                </TableCell>
                <TableCell>
                  {composite._labels?.tax_profile_id || (composite.tax_profile_id ? `ID: ${composite.tax_profile_id}` : '-')}
                </TableCell>
                <TableCell>${composite.cost?.toFixed(4) || 'N/A'}</TableCell>
                <TableCell className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(composite)}
                  >
                    Edit {/* Changed to Edit Surcharge Profile */}
                  </Button>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        View Composition
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Composition for {composite.description}</DialogTitle>
                      </DialogHeader>
                      <CompositionViewer composition={composite.composition} total_cost={costWithOutSurcharge} />
                    </DialogContent>
                  </Dialog>
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                  >
                    <Link to={`/composition-edit/${composite.id}`}>
                      Edit Composition
                    </Link>
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => onDelete(composite.id!)}
                    disabled={hasComposition}
                    title={hasComposition ? 'Cannot delete while has composition' : undefined}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
      />
    </div>
  );
};

export default CompositesTable;